import React, { useState, useEffect } from 'react';
import './LoginPage.css';
import {
    Flex,
    Container,
    Card,
    Heading,
    TextField,
    Button,
    Text,
    Separator,
    Callout
} from '@radix-ui/themes';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from './firebase';
import { apiService } from './services/api';
import logo from './logo.png';

interface LoginPageProps {
    onLoginSuccess: (user: any) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState<'mobile' | 'otp'>('mobile');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);
    const [confirmationResult, setConfirmationResult] = useState<any>(null);

    useEffect(() => {
        // Initialize reCAPTCHA
        if (!recaptchaVerifier) {
            const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                'size': 'invisible',
                'callback': () => {
                    console.log('reCAPTCHA solved');
                }
            });
            setRecaptchaVerifier(verifier);
        }
    }, [recaptchaVerifier]);

    const validateMobileNumber = (mobile: string): boolean => {
        const mobileRegex = /^[6-9]\d{9}$/;
        return mobileRegex.test(mobile);
    };

    // Step 1: Verify Mobile Number Button Click
    const handleVerifyMobile = async () => {
        setError('');
        setLoading(true);

        try {
            // Validate mobile number format
            if (!validateMobileNumber(mobile)) {
                setError('Please enter a valid 10-digit mobile number');
                setLoading(false);
                return;
            }

            // Step 2: Backend Validation
            const validationResponse = await apiService.verifyMobile(mobile);

            if (!validationResponse.success) {
                setError(validationResponse.message || 'Mobile number validation failed');
                setLoading(false);
                return;
            }

            // Step 3: Firebase OTP
            if (recaptchaVerifier) {
                const phoneNumber = `+91${mobile}`; // Prepend India code
                const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
                setConfirmationResult(confirmation);
                setStep('otp');
            }
        } catch (error: any) {
            console.error('Error in mobile verification:', error);
            setError(error.message || 'Failed to verify mobile number. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Step 4: OTP Verification
    const handleOtpSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Verify OTP with Firebase
            const result = await confirmationResult.confirm(otp);

            if (result.user) {
                // Step 5: Backend Login
                const loginResponse = await apiService.loginWithMobile(mobile);

                if (loginResponse.success) {
                    // Step 6: Token Storage
                    apiService.storeToken(loginResponse.token);

                    // Step 7: Home Page (via callback)
                    onLoginSuccess(loginResponse.user);
                } else {
                    setError('Login failed. Please try again.');
                }
            }
        } catch (error: any) {
            console.error('Error verifying OTP:', error);
            setError('Invalid OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToMobile = () => {
        setStep('mobile');
        setOtp('');
        setError('');
    };

    return (
        <Container size="2" className="login-container light-theme">
            <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
                <Card size="3" className="login-card">
                    <Flex direction="column" gap="4" p="6">
                        {/* Branding: Logo and App Name at the top of the login card */}
                        <Flex direction="column" align="center" gap="3" style={{ marginBottom: 16 }}>
                            {/* Organization Logo */}
                            <img src={logo} alt="Wado Kai Karate Logo" style={{ width: 64, height: 64 }} />
                            {/* Welcome Back Text */}
                            <Heading size="5" align="center" style={{ marginTop: 8, marginBottom: 0, color: '#444' }}>Welcome Back</Heading>
                            {/* App Name */}
                            <Heading size="6" align="center">Wado Kai Student Space</Heading>
                        </Flex>

                        <Separator size="4" />

                        {error && (
                            <Callout.Root color="red" className="error-callout">
                                <Callout.Text>{error}</Callout.Text>
                            </Callout.Root>
                        )}

                        {step === 'mobile' ? (
                            <Flex direction="column" gap="3">
                                <Flex direction="column" gap="2">
                                    <Text size="2" weight="bold">
                                        Mobile Number
                                    </Text>
                                    <TextField.Root
                                        size="3"
                                        placeholder="Enter 10-digit mobile number"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value)}
                                        maxLength={10}
                                    />
                                </Flex>

                                <Button
                                    size="3"
                                    disabled={loading || !mobile}
                                    onClick={handleVerifyMobile}
                                    style={{ marginTop: '8px' }}
                                >
                                    {loading ? 'Verifying...' : 'Verify Mobile Number'}
                                </Button>
                            </Flex>
                        ) : (
                            <form onSubmit={handleOtpSubmit}>
                                <Flex direction="column" gap="3">
                                    <Flex direction="column" gap="2">
                                        <Text size="2" weight="bold">
                                            OTP Code
                                        </Text>
                                        <TextField.Root
                                            size="3"
                                            placeholder="Enter 6-digit OTP"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            required
                                            maxLength={6}
                                        />
                                    </Flex>

                                    <Flex gap="2">
                                        <Button type="button" size="3" variant="outline" onClick={handleBackToMobile}>
                                            Back
                                        </Button>
                                        <Button type="submit" size="3" disabled={loading || !otp} style={{ flex: 1 }}>
                                            {loading ? 'Verifying...' : 'Verify & Login'}
                                        </Button>
                                    </Flex>
                                </Flex>
                            </form>
                        )}

                        {/* reCAPTCHA container */}
                        <div id="recaptcha-container"></div>
                    </Flex>
                </Card>
            </Flex>
        </Container>
    );
};

export default LoginPage; 